import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type OpenAPISpec = Record<string, any> | null;

function ApiDocsPage() {
  const { t } = useTranslation('apiDocs');
  const [spec, setSpec] = useState<OpenAPISpec>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadSpec() {
      try {
        setIsLoading(true);
        setError(null);

        const res = await fetch('/api/openapi', {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!res.ok) {
          throw new Error(`${t('errors.loadFailed')}: ${res.status} ${res.statusText}`);
        }

        const json = await res.json();
        if (isMounted) {
          setSpec(json);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to load OpenAPI spec', err);
          setError(err instanceof Error ? err.message : t('errors.unknown'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSpec();

    return () => {
      isMounted = false;
    };
  }, [t]);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </div>
        <div className="flex gap-2">
          <a
            href="/api/openapi"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-md border px-3 py-1 text-sm font-medium hover:bg-muted"
          >
            {t('openApiJsonButton')}
          </a>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
          {t('loading')}
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {t('errors.prefix')}: {error}
        </div>
      )}

      {!isLoading && !error && spec && (
        <div className="flex flex-col gap-4">
          <div className="rounded-md border bg-background p-4 text-sm">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <div className="text-base font-medium">
                  {spec.info?.title ?? t('defaultApiTitle')}
                  {spec.info?.version ? (
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                      v{spec.info.version}
                    </span>
                  ) : null}
                </div>
                {spec.info?.description && (
                  <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                    {spec.info.description}
                  </p>
                )}
              </div>
              {Array.isArray(spec.servers) && spec.servers.length > 0 && (
                <div className="text-right">
                  <div className="text-xs font-medium text-muted-foreground">
                    {t('servers.title')}
                  </div>
                  <div className="text-xs">{spec.servers.map((s: any) => s.url).join(', ')}</div>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t('lightweightView')}</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
              <div className="text-sm font-medium">{t('endpoints.title')}</div>
              <div className="max-h-[480px] space-y-1 overflow-auto text-xs">
                {spec.paths ? (
                  Object.entries(spec.paths).map(([path, methods]: [string, any]) => {
                    const methodEntries = Object.entries(methods ?? {}) as [string, any][];
                    return methodEntries.map(([method, op]) => (
                      <div
                        key={`${method.toUpperCase()} ${path}`}
                        className="flex items-start gap-2 rounded border bg-muted/40 p-2"
                      >
                        <span className="inline-flex min-w-[3.2rem] justify-center rounded bg-primary/10 px-1.5 py-0.5 text-[0.7rem] font-semibold uppercase text-primary">
                          {method}
                        </span>
                        <div className="flex-1">
                          <div className="font-mono text-[0.7rem]">{path}</div>
                          {op?.summary && (
                            <div className="text-[0.7rem] text-muted-foreground">{op.summary}</div>
                          )}
                        </div>
                      </div>
                    ));
                  })
                ) : (
                  <div className="text-muted-foreground">{t('endpoints.noPaths')}</div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{t('rawJson.title')}</div>
                <div className="text-[0.7rem] text-muted-foreground">{t('rawJson.preview')}</div>
              </div>
              <div className="max-h-[480px] overflow-auto rounded border bg-muted/40 p-2 text-xs font-mono">
                <pre className="whitespace-pre-wrap text-[0.65rem]">
                  {JSON.stringify(spec, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// @ts-expect-error route tree not yet generated
export const Route = createFileRoute('/_authenticated/api-docs')({
  component: ApiDocsPage,
});
